'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import * as Yup from 'yup';
import isEmpty from 'lodash/isEmpty';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import { useForm, yupResolver } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// UI lib components
import {
  Alert,
  Button,
  Checkbox,
  Container,
  Fieldset,
  ScrollArea,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import ContentLoader from '@shared/ui-kit/molecules/ContentLoader';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// API & Types
import {
  useGetRolesQuery,
  useListGroupsQuery,
  useAddUsersToWorkspaceMutation,
} from '@app-types/blackroom-api';
import { DialogUserBulkInviteProps } from './types';
import useGroupDisplayName from '@app/features/Group/hooks/useGroupDisplayName';
import useRoleDisplayName from '@features/Role/hooks/useRoleDisplayName';

// Assets
import { IconAlertTriangle } from '@tabler/icons-react';

// Style
import * as classes from './styles/index.css';
import { getAddUsersError } from './errors';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type InviteeEntry = {
  email: string;
  roleId: string;
  groupId: string;
};

type DialogUserBulkInviteFormSchema = {
  emails: string[];
  invitees: InviteeEntry[];
  '2fa': boolean;
  invitation: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogUserBulkInvite({ ...props }: DialogUserBulkInviteProps) {
  /* ********************************** HOOKS ********************************* */

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const workspace = useWorkspace();
  const client = useApolloClient();
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API
  const {
    data: groupData,
    loading: loadingGroups,
    error: groupsError,
    refetch: refetchGroups,
  } = useListGroupsQuery({
    variables: {
      filter: { workspaceId: workspace.id },
    },
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: rolesData,
    loading: loadingRoles,
    error: rolesError,
    refetch: refetchRoles,
  } = useGetRolesQuery({
    variables: { workspaceId: workspace.id },
  });
  const [addUsersToWorkspace] = useAddUsersToWorkspaceMutation();

  // Localization
  const { t } = useTranslation();
  const { getDisplayName } = useGroupDisplayName();
  const { getRoleDisplayName } = useRoleDisplayName();

  // Form
  const formSchema = Yup.object().shape({
    emails: Yup.array()
      .of(Yup.string().email(t('Common:validation.email')).required())
      .min(1, t('Common:validation.required'))
      .required(t('Common:validation.required')),
    invitees: Yup.array().of(
      Yup.object({
        roleId: Yup.string()
          .required(t('User:DialogUserBulkInvite.errors.roleRequired'))
          .oneOf(
            (rolesData?.roles ?? []).map((role) => role.id),
            t('User:DialogUserBulkInvite.errors.roleRequired'),
          ),
        groupId: Yup.string().oneOf(
          ['', ...(groupData?.groups.nodes ?? []).map((group) => group.id)],
          t('User:DialogUserBulkInvite.errors.groupUnavailable'),
        ),
      }),
    ),
  });
  const form = useForm<DialogUserBulkInviteFormSchema>({
    initialValues: {
      emails: [],
      invitees: [],
      '2fa': false,
      invitation: '',
    },
    validate: yupResolver(formSchema),
  });

  /* ***************************** LOCAL VARIABLES **************************** */

  const groups = groupData?.groups.nodes ?? [];
  const roles = rolesData?.roles ?? [];
  const hasGroups = groups.length > 0;
  const hasOptionsError = !!groupsError || !!rolesError || roles.length === 0;

  const defaultRoleId = useMemo(
    () => roles.find((r) => !r.isAdmin)?.id ?? '',
    [roles],
  );

  const roleSelectData = useMemo(
    () =>
      roles.map((r) => ({ value: r.id, label: getRoleDisplayName(r.name) })),
    [roles, getRoleDisplayName],
  );

  const groupSelectData = useMemo(
    () => groups.map((g) => ({ value: g.id, label: getDisplayName(g) })),
    [groups, getDisplayName],
  );

  const isValidEmail = useCallback(
    (email: string) => Yup.string().email().required().isValidSync(email),
    [],
  );
  const hasInvalidEmails = form.values.emails.some((e) => !isValidEmail(e));

  /* ********************************* EFFECTS ******************************** */

  // Sync invitees array when emails change
  useEffect(() => {
    const currentEmails = form.values.emails;
    const currentInvitees = form.values.invitees;

    const existingByEmail = new Map(
      currentInvitees.map((inv) => [inv.email, inv]),
    );

    const newInvitees = currentEmails.map(
      (email) =>
        existingByEmail.get(email) ?? {
          email,
          roleId: defaultRoleId,
          groupId: '',
        },
    );

    // Only update if the invitees actually changed
    if (
      newInvitees.length !== currentInvitees.length ||
      newInvitees.some(
        (inv, i) =>
          inv.email !== currentInvitees[i]?.email ||
          inv.roleId !== currentInvitees[i]?.roleId ||
          inv.groupId !== currentInvitees[i]?.groupId,
      )
    ) {
      form.setFieldValue('invitees', newInvitees);
      // Keep errors attached to their invitee when rows are removed or reordered.
      const indexByEmail = new Map(
        newInvitees.map((invitee, index) => [invitee.email, index]),
      );
      form.setErrors((errors) =>
        Object.fromEntries(
          Object.entries(errors).flatMap(([path, error]) => {
            const match = /^invitees\.(\d+)(\..*)?$/.exec(path);
            if (!match) return [[path, error]];

            const email = currentInvitees[Number(match[1])]?.email;
            const index = indexByEmail.get(email);
            return index === undefined
              ? []
              : [[`invitees.${index}${match[2] ?? ''}`, error]];
          }),
        ),
      );
    }
  }, [form.values.emails, form.values.invitees, defaultRoleId]);

  /* ******************************** CALLBACKS ******************************* */

  const isRoleWithoutGroup = useCallback(
    (roleId: string) => {
      const role = roles.find((r) => r.id === roleId);
      if (!role) return false;
      return role.isAdmin;
    },
    [roles],
  );

  const handleRoleChange = useCallback(
    (index: number, roleId: string | null) => {
      if (!roleId) return;
      form.setFieldValue(`invitees.${index}.roleId`, roleId);
      // Clear group if role doesn't need group assignment
      if (isRoleWithoutGroup(roleId)) {
        form.setFieldValue(`invitees.${index}.groupId`, '');
      }
    },
    [form, isRoleWithoutGroup],
  );

  const handleGroupChange = useCallback(
    (index: number, groupId: string | null) => {
      form.setFieldValue(`invitees.${index}.groupId`, groupId ?? '');
    },
    [form],
  );

  const onConfirm = useCallback(async () => {
    if (
      submittingRef.current ||
      loadingGroups ||
      loadingRoles ||
      hasOptionsError
    )
      return;
    const { hasErrors } = form.validate();
    if (
      hasErrors ||
      hasInvalidEmails ||
      form.values.invitees.length !== form.values.emails.length
    )
      return;

    submittingRef.current = true;
    setIsSubmitting(true);
    const activeDialog = dialogStoreVar();
    if (activeDialog?.dialogIdentifier === 'dialog-user-bulk-invite') {
      dialogStoreVar({
        ...activeDialog,
        dialogProps: {
          ...activeDialog.dialogProps,
          modalProps: {
            ...activeDialog.dialogProps?.modalProps,
            closeOnClickOutside: false,
            closeOnEscape: false,
          },
        },
      });
    }
    let invitationFailure: string[] | undefined;
    try {
      try {
        await addUsersToWorkspace({
          variables: {
            workspaceId: workspace.id,
            addUsersToWorkspaceInput: form.values.invitees.map((inv) => ({
              userEmail: inv.email,
              roleId: inv.roleId || undefined,
              groupId: inv.groupId || undefined,
            })),
          },
        });
      } catch (error) {
        const failure = getAddUsersError(error);
        if (failure.kind === 'invitations') {
          invitationFailure = failure.emails;
        } else {
          if (failure.kind !== 'handled') {
            showNotification({
              title: t('Common:errors.genericTitle'),
              message: t(
                `User:DialogUserBulkInvite.errors.${failure.kind === 'network' ? 'resultUnknown' : 'saveFailed'}`,
              ),
              color: 'blackroomRed',
              autoClose: false,
            });
          }
          return;
        }
      }

      // A refresh failure must never be presented as a failed addition.
      let refreshFailed = false;
      try {
        await client.refetchQueries({ include: ['GetUsers'] });
      } catch {
        refreshFailed = true;
      }

      dialogStore?.closeDialog?.('dialog-user-bulk-invite');
      const hasWarning = invitationFailure !== undefined || refreshFailed;
      showNotification({
        title: t('User:DialogUserBulkInvite.feedback.addedTitle'),
        message: (
          <Stack gap="xs">
            <Text size="sm">
              {t(
                invitationFailure !== undefined
                  ? 'User:DialogUserBulkInvite.feedback.invitationFailed'
                  : 'User:DialogUserBulkInvite.feedback.added',
              )}
            </Text>
            {!!invitationFailure?.length && (
              <Text size="sm">{invitationFailure.join(', ')}</Text>
            )}
            {refreshFailed && (
              <Text size="sm">
                {t('User:DialogUserBulkInvite.feedback.refreshFailed')}
              </Text>
            )}
          </Stack>
        ),
        color: hasWarning ? 'blackroomOrange' : 'blackroomGreen',
        autoClose: hasWarning ? false : 4000,
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      const currentDialog = dialogStoreVar();
      if (
        activeDialog?.dialogIdentifier === 'dialog-user-bulk-invite' &&
        currentDialog &&
        currentDialog.closeDialog === activeDialog.closeDialog
      ) {
        dialogStoreVar({
          ...currentDialog,
          dialogProps: {
            ...currentDialog.dialogProps,
            modalProps: {
              ...currentDialog.dialogProps?.modalProps,
              closeOnClickOutside:
                activeDialog.dialogProps?.modalProps?.closeOnClickOutside ??
                true,
              closeOnEscape:
                activeDialog.dialogProps?.modalProps?.closeOnEscape ?? true,
            },
          },
        });
      }
    }
  }, [
    form,
    hasInvalidEmails,
    loadingGroups,
    loadingRoles,
    hasOptionsError,
    dialogStore,
    workspace,
    addUsersToWorkspace,
    client,
    t,
  ]);

  /* ******************************** RENDERING ******************************* */

  if (loadingGroups || loadingRoles) {
    return (
      <Container className={classes.dialogUserBulkInvite}>
        <Stack align="stretch" gap="lg">
          <ContentLoader />
        </Stack>
      </Container>
    );
  }

  return (
    <Container className={classes.dialogUserBulkInvite}>
      <form onSubmit={(event) => event.preventDefault()}>
        <Stack gap="xl">
          {hasOptionsError && (
            <Alert color="blackroomRed">
              <Stack gap="xs">
                <Text size="sm">
                  {t('User:DialogUserBulkInvite.errors.optionsFailed')}
                </Text>
                <Button
                  variant="subtle"
                  onClick={() => {
                    void Promise.allSettled([refetchGroups(), refetchRoles()]);
                  }}
                >
                  {t('User:DialogUserBulkInvite.retry')}
                </Button>
              </Stack>
            </Alert>
          )}
          <Fieldset variant="unstyled" disabled={isSubmitting}>
            <Stack align="stretch" gap="xs" pt={5}>
              <TagsInput
                size="md"
                required
                label={t('User:DialogUserBulkInvite.form.fields.emails.label')}
                onKeyDown={(event) => {
                  if (event.code === 'Space') event.preventDefault();
                }}
                splitChars={[',', ' ']}
                classNames={{
                  root: classes.formInputRoot,
                  input: classes.formEmailInput,
                }}
                {...form.getInputProps('emails')}
                onChange={(emails) =>
                  form.setFieldValue('emails', [
                    ...new Set(
                      emails.map((email) => email.trim().toLowerCase()),
                    ),
                  ])
                }
                description={t(
                  'User:DialogUserBulkInvite.form.fields.emails.description',
                )}
              />

              {form.values.invitees.length > 0 && (
                <Stack gap="xs">
                  <div className={classes.inviteeRow}>
                    <Text size="sm" fw={500} className={classes.emailColumn}>
                      {t('User:DialogUserBulkInvite.form.fields.emails.label')}
                    </Text>
                    <Text size="sm" fw={500} className={classes.roleColumn}>
                      {t('User:DialogUserBulkInvite.form.fields.role.label')}
                    </Text>
                    <Text size="sm" fw={500} className={classes.groupColumn}>
                      {t('User:DialogUserBulkInvite.form.fields.group.label')}
                    </Text>
                  </div>

                  <ScrollArea.Autosize mah={240}>
                    <Stack gap="xs">
                      {form.values.invitees.map((invitee, index) => {
                        const skipGroup = isRoleWithoutGroup(invitee.roleId);
                        const groupDisabled = skipGroup || !hasGroups;
                        const emailInvalid = !isValidEmail(invitee.email);

                        const groupSelect = (
                          <Select
                            size="sm"
                            data={groupSelectData}
                            value={invitee.groupId || null}
                            onChange={(value) =>
                              handleGroupChange(index, value)
                            }
                            placeholder={
                              skipGroup
                                ? t(
                                    'User:DialogUserBulkInvite.form.fields.group.adminAllGroups',
                                  )
                                : t(
                                    'User:DialogUserBulkInvite.form.fields.group.placeholder',
                                  )
                            }
                            disabled={groupDisabled}
                            error={form.errors[`invitees.${index}.groupId`]}
                            clearable
                          />
                        );

                        return (
                          <div
                            key={invitee.email}
                            className={classes.inviteeRow}
                          >
                            <div className={classes.emailColumn}>
                              {emailInvalid ? (
                                <Tooltip
                                  label={t('Common:validation.email')}
                                  position="top"
                                  withArrow
                                  withinPortal
                                >
                                  <Text
                                    size="sm"
                                    c="red"
                                    className={classes.emailText}
                                  >
                                    <IconAlertTriangle
                                      size="0.875rem"
                                      style={{
                                        verticalAlign: 'middle',
                                        marginRight: 4,
                                      }}
                                    />
                                    {invitee.email}
                                  </Text>
                                </Tooltip>
                              ) : (
                                <Text
                                  size="sm"
                                  className={classes.emailText}
                                  title={invitee.email}
                                >
                                  {invitee.email}
                                </Text>
                              )}
                            </div>

                            <Select
                              size="sm"
                              data={roleSelectData}
                              value={invitee.roleId}
                              onChange={(value) =>
                                handleRoleChange(index, value)
                              }
                              allowDeselect={false}
                              className={classes.roleColumn}
                              error={form.errors[`invitees.${index}.roleId`]}
                            />

                            <div className={classes.groupColumn}>
                              {!skipGroup && !hasGroups ? (
                                <Tooltip
                                  label={t(
                                    'User:DialogUserBulkInvite.form.fields.group.noGroupsTooltip',
                                  )}
                                  position="top"
                                  withArrow
                                  withinPortal
                                >
                                  {groupSelect}
                                </Tooltip>
                              ) : (
                                groupSelect
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </Stack>
                  </ScrollArea.Autosize>
                </Stack>
              )}

              {import.meta.env.VITE_APP_ACCOUNT_2FA !== 'false' && (
                <Checkbox
                  label={t('User:DialogUserBulkInvite.form.fields.2fa.label')}
                  radius="xs"
                  {...form.getInputProps('2fa')}
                />
              )}

              {import.meta.env.VITE_APP_ACCOUNT_CUSTOM_INVITATION !==
                'false' && (
                <Textarea
                  label={t(
                    'User:DialogUserBulkInvite.form.fields.invitation.label',
                  )}
                  placeholder={
                    t(
                      'User:DialogUserBulkInvite.form.fields.invitation.placeholder',
                      {
                        workspaceName: workspace.name,
                      },
                    ) ?? ''
                  }
                  {...form.getInputProps('invitation')}
                />
              )}
            </Stack>
          </Fieldset>

          <ConfirmBar
            labelProps={{
              confirm: t('User:DialogUserBulkInvite.confirm'),
              cancel: t('Common:cta.cancel'),
            }}
            onConfirm={onConfirm}
            saving={isSubmitting}
            disabled={
              isSubmitting ||
              hasOptionsError ||
              !isEmpty(form.errors) ||
              !form.values.emails ||
              form.values.emails.length === 0 ||
              hasInvalidEmails
            }
            onCancel={() => {
              if (!submittingRef.current)
                dialogStore?.closeDialog?.('dialog-user-bulk-invite');
            }}
          />
        </Stack>
      </form>
    </Container>
  );
}

export default DialogUserBulkInvite;
